/**
 * Local Storage Backend
 * Ported from: innovation-claude/packages/storage/src/backends/local.rs
 *
 * Uses browser localStorage for persistent storage (survives page reloads and browser restarts)
 */

import { StorageError } from '../StorageError'

/**
 * LocalBackend - Uses browser localStorage
 *
 * Storage characteristics:
 * - Persistent across browser sessions
 * - ~5-10MB quota per origin
 * - Synchronous API (but we wrap in async for interface compatibility)
 * - Shared across all tabs/windows from same origin
 */
export class LocalBackend {
    /**
     * Save data to localStorage
     */
    async save(key: string, json: string): Promise<void> {
        try {
            localStorage.setItem(key, json)
        } catch (error) {
            if (error instanceof Error && error.name === 'QuotaExceededError') {
                throw StorageError.quotaExceeded(`localStorage quota exceeded for key: ${key}`)
            }
            throw StorageError.platform(`Failed to save to localStorage: ${error}`)
        }
    }

    /**
     * Load data from localStorage
     */
    async load(key: string): Promise<string | null> {
        try {
            return localStorage.getItem(key)
        } catch (error) {
            throw StorageError.platform(`Failed to load from localStorage: ${error}`)
        }
    }

    /**
     * Delete data from localStorage
     */
    async delete(key: string): Promise<void> {
        try {
            localStorage.removeItem(key)
        } catch (error) {
            throw StorageError.platform(`Failed to delete from localStorage: ${error}`)
        }
    }

    /**
     * Check if localStorage is available
     */
    static isAvailable(): boolean {
        try {
            const test = '__storage_test__'
            localStorage.setItem(test, test)
            localStorage.removeItem(test)
            return true
        } catch {
            return false
        }
    }
}
